import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DocumentUpload = ({ 
  isOpen, 
  onClose, 
  onUpload, 
  targetFolder,
  allowedFormats = ['doc', 'docx', 'xls', 'xlsx', 'pdf', 'jpg', 'jpeg', 'png', 'txt']
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const validateFile = (file) => {
    const maxSize = 50 * 1024 * 1024; // 50MB
    const fileExtension = file?.name?.split('.')?.pop()?.toLowerCase();
    
    if (!allowedFormats?.includes(fileExtension)) {
      return `File type .${fileExtension} is not supported. Allowed formats: ${allowedFormats?.join(', ')}`;
    }
    
    if (file?.size > maxSize) {
      return 'File size must be less than 50MB';
    }
    
    return null;
  };

  const processFiles = (files) => {
    const validFiles = [];
    const errors = [];
    
    Array.from(files)?.forEach(file => {
      const error = validateFile(file);
      if (error) {
        errors?.push({ file: file?.name, error });
      } else {
        validFiles?.push({
          id: Date.now() + Math.random(),
          file,
          name: file?.name,
          size: file?.size,
          type: file?.name?.split('.')?.pop()?.toLowerCase(),
          progress: 0,
          status: 'pending', // pending, uploading, completed, error
          error: null
        });
      }
    });
    
    if (errors?.length > 0) {
      alert(`Some files could not be added:\n${errors?.map(e => `${e?.file}: ${e?.error}`)?.join('\n')}`);
    }
    
    setUploadQueue(prev => [...prev, ...validFiles]);
  };

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === 'dragenter' || e?.type === 'dragover') {
      setDragActive(true);
    } else if (e?.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);
    
    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.[0]) {
      processFiles(e?.dataTransfer?.files);
    }
  };

  const handleFileSelect = (e) => {
    if (e?.target?.files && e?.target?.files?.[0]) {
      processFiles(e?.target?.files);
    }
  };

  const removeFromQueue = (id) => {
    setUploadQueue(prev => prev?.filter(item => item?.id !== id));
  };

  const simulateUpload = async (item) => {
    return new Promise((resolve) => {
      let progress = 0;
      const interval = setInterval(() => {
        progress += Math.random() * 30;
        if (progress >= 100) {
          progress = 100;
          clearInterval(interval);
          resolve();
        }
        
        setUploadQueue(prev => prev.map(queueItem => 
          queueItem.id === item.id 
            ? { ...queueItem, progress, status: progress === 100 ? 'completed' : 'uploading' }
            : queueItem
        ));
      }, 200);
    });
  };

  const handleUpload = async () => {
    setUploading(true);
    
    const pendingItems = uploadQueue?.filter(item => item?.status === 'pending');
    
    for (const item of pendingItems) {
      setUploadQueue(prev => prev?.map(queueItem => 
        queueItem?.id === item?.id 
          ? { ...queueItem, status: 'uploading' }
          : queueItem
      ));
      
      try {
        await simulateUpload(item);
        // Call the actual upload function
        await onUpload(item?.file, targetFolder);
      } catch (error) {
        setUploadQueue(prev => prev?.map(queueItem => 
          queueItem?.id === item?.id 
            ? { ...queueItem, status: 'error', error: error?.message }
            : queueItem
        ));
      }
    }
    
    setUploading(false);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return 'Clock';
      case 'uploading': return 'Upload';
      case 'completed': return 'CheckCircle';
      case 'error': return 'XCircle';
      default: return 'File';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-muted-foreground';
      case 'uploading': return 'text-primary';
      case 'completed': return 'text-success';
      case 'error': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg border border-border w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Upload Documents</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Upload to: {targetFolder?.name || 'Uncategorized'}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            iconName="X"
            iconSize={20}
            onClick={onClose}
          />
        </div>

        {/* Upload Area */}
        <div className="p-6 flex-1 overflow-hidden flex flex-col">
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive 
                ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <Icon name="Upload" size={48} className="mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">
              Drop files here or click to browse
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Supported formats: {allowedFormats?.join(', ')} • Max size: 50MB
            </p>
            <Button
              variant="outline"
              iconName="FolderOpen"
              iconPosition="left"
              onClick={() => fileInputRef?.current?.click()}
            >
              Browse Files
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={allowedFormats?.map(format => `.${format}`)?.join(',')}
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {/* Upload Queue */}
          {uploadQueue?.length > 0 && (
            <div className="mt-6 flex-1 overflow-hidden flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-foreground">
                  Upload Queue ({uploadQueue?.length} files)
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Trash2"
                  iconSize={16}
                  onClick={() => setUploadQueue([])}
                  disabled={uploading}
                >
                  Clear All
                </Button>
              </div>

              <div className="flex-1 overflow-y-auto space-y-2 max-h-64">
                {uploadQueue?.map((item) => (
                  <div
                    key={item?.id}
                    className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg"
                  >
                    <Icon 
                      name={getStatusIcon(item?.status)} 
                      size={20} 
                      className={getStatusColor(item?.status)}
                    />
                    
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-foreground truncate">
                        {item?.name}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-muted-foreground">
                          {formatFileSize(item?.size)}
                        </span>
                        {item?.status === 'uploading' && (
                          <span className="text-xs text-primary">
                            {Math.round(item?.progress)}%
                          </span>
                        )}
                        {item?.error && (
                          <span className="text-xs text-error">{item?.error}</span>
                        )}
                      </div>
                      
                      {item?.status === 'uploading' && (
                        <div className="w-full bg-muted rounded-full h-1.5 mt-2">
                          <div
                            className="bg-primary h-1.5 rounded-full transition-all"
                            style={{ width: `${item?.progress}%` }}
                          />
                        </div>
                      )}
                    </div>

                    {item?.status === 'pending' && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="w-8 h-8"
                        iconName="X"
                        iconSize={16}
                        onClick={() => removeFromQueue(item?.id)}
                        disabled={uploading}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          <p className="text-sm text-muted-foreground">
            {uploadQueue?.filter(item => item?.status === 'completed')?.length} of {uploadQueue?.length} files uploaded
          </p>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={uploading}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              iconName="Upload"
              iconPosition="left"
              onClick={handleUpload}
              disabled={uploadQueue?.filter(item => item?.status === 'pending')?.length === 0 || uploading}
              loading={uploading}
            >
              Upload Files
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentUpload;